import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Field, Input, Select, Spinner, Table, EmptyState } from '../../components/ui'
import { clp, formatDateShort, formatDateTime, HEALTH_ROLE_LABELS } from '../../utils/format'
import type { FeeAccrual, FeeSettlement, Professional } from '../../types/api'

const ACCRUAL_STATUS: Record<string, { label: string; className: string }> = {
  ACCRUED: { label: 'Devengado', className: 'bg-amber-100 text-amber-800' },
  SETTLED: { label: 'Liquidado', className: 'bg-sky-100 text-sky-800' },
  PAID: { label: 'Pagado', className: 'bg-emerald-100 text-emerald-800' },
}

export function HonorariosPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAccountant = user?.role === 'ACCOUNTANT'
  const [error, setError] = useState('')
  const [form, setForm] = useState(() => {
    const start = new Date()
    start.setDate(1)
    const end = new Date(start)
    end.setMonth(end.getMonth() + 1)
    return {
      professionalId: '',
      periodStart: start.toISOString().slice(0, 10),
      periodEnd: end.toISOString().slice(0, 10),
      taxRetentionPercent: '14.5',
    }
  })

  const accruals = useQuery({
    queryKey: ['fee-accruals'],
    queryFn: async () => (await api.get<FeeAccrual[]>('/accounting/fee-accruals')).data,
  })
  const settlements = useQuery({
    queryKey: ['settlements'],
    queryFn: async () => (await api.get<FeeSettlement[]>('/accounting/settlements')).data,
    enabled: Boolean(user && ['ADMIN', 'ACCOUNTANT'].includes(user.role)),
  })
  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
    enabled: isAccountant,
  })

  const createSettlement = useMutation({
    mutationFn: async () =>
      (
        await api.post('/accounting/settlements', {
          professionalId: form.professionalId,
          periodStart: new Date(form.periodStart).toISOString(),
          periodEnd: new Date(form.periodEnd).toISOString(),
          taxRetentionPercent: Number(form.taxRetentionPercent),
        })
      ).data,
    onSuccess: () => {
      setError('')
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
      queryClient.invalidateQueries({ queryKey: ['fee-accruals'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const paySettlement = useMutation({
    mutationFn: async (id: string) => (await api.post(`/accounting/settlements/${id}/pay`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settlements'] })
      queryClient.invalidateQueries({ queryKey: ['fee-accruals'] })
      queryClient.invalidateQueries({ queryKey: ['ledger'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">Honorarios</h1>

      <Card title={user?.role === 'DOCTOR' ? 'Mis honorarios devengados' : 'Honorarios devengados'}>
        {accruals.isLoading ? (
          <Spinner />
        ) : (accruals.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin honorarios devengados aún. Se generan automáticamente al cobrar atenciones." />
        ) : (
          <Table headers={['Fecha', 'Profesional', 'Atención', 'Regla', 'Base', 'Honorario', 'Estado']}>
            {(accruals.data ?? []).map((a) => (
              <tr key={a.id}>
                <td className="px-2 py-2">{formatDateTime(a.createdAt)}</td>
                <td className="px-2 py-2">
                  {a.professional.firstName} {a.professional.lastName}
                  <span className="block text-xs text-gray-400">{HEALTH_ROLE_LABELS[a.professional.healthRole]}</span>
                </td>
                <td className="px-2 py-2 text-gray-600">{a.charge.description}</td>
                <td className="px-2 py-2 text-xs text-gray-500">
                  {a.feeRule ? (a.feeRule.mode === 'PERCENTAGE' ? `${a.feeRule.value}%` : `Fijo ${clp(a.feeRule.value)}`) : '—'}
                </td>
                <td className="px-2 py-2">{clp(a.baseAmount)}</td>
                <td className="px-2 py-2 font-medium">{clp(a.amount)}</td>
                <td className="px-2 py-2">
                  <Badge className={ACCRUAL_STATUS[a.status].className}>{ACCRUAL_STATUS[a.status].label}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {isAccountant && (
        <Card title="Generar liquidación (estado de pago)">
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Profesional">
              <Select value={form.professionalId} onChange={(e) => setForm({ ...form, professionalId: e.target.value })}>
                <option value="">Selecciona…</option>
                {(professionals.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </Select>
            </Field>
            <Field label="Desde">
              <Input type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} />
            </Field>
            <Field label="Hasta">
              <Input type="date" value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} />
            </Field>
            <Field label="Retención %" hint="2ª categoría">
              <Input type="number" step="0.5" value={form.taxRetentionPercent} onChange={(e) => setForm({ ...form, taxRetentionPercent: e.target.value })} />
            </Field>
            <Button disabled={!form.professionalId || createSettlement.isPending} onClick={() => createSettlement.mutate()}>
              Generar
            </Button>
          </div>
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </Card>
      )}

      {user && ['ADMIN', 'ACCOUNTANT'].includes(user.role) && (
        <Card title="Liquidaciones">
          {(settlements.data?.length ?? 0) === 0 ? (
            <EmptyState message="Sin liquidaciones generadas." />
          ) : (
            <Table headers={['Profesional', 'Periodo', 'Bruto', 'Retención', 'Líquido', 'Estado', '']}>
              {(settlements.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="px-2 py-2">
                    {s.professional.firstName} {s.professional.lastName}
                    <span className="block text-xs text-gray-400">{HEALTH_ROLE_LABELS[s.professional.healthRole]}</span>
                  </td>
                  <td className="px-2 py-2">{formatDateShort(s.periodStart)} → {formatDateShort(s.periodEnd)}</td>
                  <td className="px-2 py-2">{clp(s.grossAmount)}</td>
                  <td className="px-2 py-2 text-red-600">−{clp(s.taxRetention)}</td>
                  <td className="px-2 py-2 font-medium">{clp(s.netAmount)}</td>
                  <td className="px-2 py-2">
                    <Badge className={s.status === 'PAID' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                      {s.status === 'PAID' ? 'Pagada' : 'Borrador'}
                    </Badge>
                  </td>
                  <td className="px-2 py-2">
                    {isAccountant && s.status !== 'PAID' && (
                      <Button variant="cta" disabled={paySettlement.isPending} onClick={() => paySettlement.mutate(s.id)}>
                        Pagar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}
    </div>
  )
}
